import {
    ColumnMetadata,
    DataGrid,
    DataGridRow,
    RowSizes,
} from "~/components/DataGrid";
import {
    RANK_COLUMN,
    RWA_ASSET_COUNT_COLUMN,
    RWA_HOLDERS_COUNT_COLUMN,
    RWA_MARKET_CAP_COLUMN,
    NAME_INDIVIDUAL_COLUMN,
    STABLECOIN_ASSET_COUNT_COLUMN,
    STABLECOIN_HOLDERS_COUNT_COLUMN,
    STABLECOIN_MARKET_CAP_COLUMN,
    PCT_DISTRIBURTED_COLUMN,
    DISTRIBUTED_MARKET_CAP_COLUMN,
    ISOLATED_MARKET_CAP_COLUMN,
} from "./columns";
import {
    Network,
    NetworkTableData,
    NetworkTableDataColumns,
    getNetworkTableData,
} from "../../types";
import {
    ApiQuery,
    QueryResponse,
    SortDirections,
    SortQuery,
} from "~/modules/filters/types";
import { sortItemsByProperty } from "~/utils/functions/sortItemsByProperty";
import { useCallback, useState } from "react";

// // // //

/**
 * NETWORK_SORT_MAP
 * Map each NetworkColumn to a key used to in-memory sort the array of items using `sortItemsByNestedProperty`
 */
// prettier-ignore
const NETWORK_SORT_MAP: Record<NetworkTableDataColumns, string> = {
    [NetworkTableDataColumns.name]: "name",
    [NetworkTableDataColumns.assetCount]: "asset_count",
    [NetworkTableDataColumns.dailyMintsDollar]: "adjusted_daily_mints_dollar.val",
    [NetworkTableDataColumns.bridgedTokenMarketCapDollar]: "bridged_token_market_cap_dollar.val",
    [NetworkTableDataColumns.bridgedTokenValueDollar]: "bridged_token_value_dollar.val",
    [NetworkTableDataColumns.dailyMintsToken]: "adjusted_daily_mints_token.val",
    [NetworkTableDataColumns.trailing30DayActiveAddressesCount]: "quarterly_active_addresses_count.val",
    [NetworkTableDataColumns.holdingAddressesCount]: "holding_addresses_count.val",
    [NetworkTableDataColumns.trailing30DayTransferVolume]: "trailing_30_day_transfer_volume.val",
    [NetworkTableDataColumns.id]: "id",
    [NetworkTableDataColumns.slug]: "slug",
    [NetworkTableDataColumns.iconUrl]: "icon_url",
    [NetworkTableDataColumns.colorHex]: "color_hex",
    [NetworkTableDataColumns.description]: "description",
    [NetworkTableDataColumns.rwaAssetCount]: "rwa_asset_count",
    [NetworkTableDataColumns.stablecoinAssetCount]: "stablecoin_asset_count",
    [NetworkTableDataColumns.rwaMarketCap]: "rwa_market_cap.val",
    [NetworkTableDataColumns.stablecoinMarketCap]: "stablecoin_market_cap.val",
    [NetworkTableDataColumns.rwaHoldersCount]: "rwa_holders_count.val",
    [NetworkTableDataColumns.stablecoinHoldersCount]: "stablecoin_holders_count.val",
    [NetworkTableDataColumns.transferabilityPct]: "transferability.pct_transferable",
    [NetworkTableDataColumns.distributedMarketCap]: "transferability.transferable",
    [NetworkTableDataColumns.isolatedMarketCap]: "transferability.non_transferable",
};

// // // //

function useInMemoryNetworkSort(props: {
    rows: NetworkTableData[];
    sort?: SortQuery;
}) {
    const {
        rows,
        sort = {
            field: NetworkTableDataColumns.distributedMarketCap,
            direction: SortDirections.desc,
        },
    } = props;

    const [query, setQuery] = useState<ApiQuery>({
        sort,
        pagination: {
            page: 1,
            perPage: 25,
        },
    });

    const sortedItems = sortItemsByProperty({
        items: rows,
        field: NETWORK_SORT_MAP[query.sort.field as NetworkTableDataColumns],
        direction: query.sort.direction,
        nullsLast: true,
    });

    function setQueryWithDefaultSort(updatedQuery: ApiQuery) {
        const sort =
            updatedQuery.sort.field && updatedQuery.sort.direction
                ? updatedQuery.sort
                : {
                      field: NetworkTableDataColumns.distributedMarketCap,
                      direction: SortDirections.desc,
                  };

        setQuery({
            ...updatedQuery,
            sort,
        });
    }

    return { query, sortedItems, setQuery: setQueryWithDefaultSort };
}

// // // //

function NetworkTableRow(props: {
    network: NetworkTableData;
    columns: ColumnMetadata<NetworkTableData, NetworkTableDataColumns>[];
    rowIndex: number;
    size: RowSizes;
    scrollLeft: number;
    defaultExpandedState: boolean;
    bordered?: boolean;
    striped?: boolean;
}) {
    const {
        network,
        columns,
        rowIndex,
        size,
        scrollLeft,
        defaultExpandedState,
        bordered,
        striped,
    } = props;
    const [expanded, setExpanded] = useState(defaultExpandedState);

    function onExpandOrig() {
        const updatedState = !expanded;

        // Track change in analytics
        // TODO - integrate analytics
        // if (updatedState) {
        //     Analytics.trackExpandTableRow({
        //         assetName: asset.name,
        //     });
        // } else {
        //     Analytics.trackCollapseTableRow({
        //         assetName: asset.name,
        //     });
        // }
        setExpanded(updatedState);
    }

    // NOTE - we define this function with useCallback to prevent redundant re-renders
    const onExpand = useCallback(() => {
        onExpandOrig();
    }, [expanded]);

    return (
        <>
            <DataGridRow<NetworkTableData, NetworkTableDataColumns>
                row={{
                    ...network,
                    expanded,
                    isParentNetwork: true,
                    rowIndex,
                    onExpand,
                }}
                key={network.id}
                columns={columns}
                size={size}
                bordered={bordered}
                scrollLeft={scrollLeft}
                striped={striped && rowIndex % 2 === 1}
            />
            {expanded && (
                <>
                    {network.child_networks.map((childNetwork) => (
                        <DataGridRow<NetworkTableData, NetworkTableDataColumns>
                            row={{
                                ...childNetwork,
                                expanded: false,
                                isParentNetwork: false,
                            }}
                            key={childNetwork.id}
                            columns={columns}
                            size={size}
                            bordered={bordered}
                            scrollLeft={scrollLeft}
                            striped={rowIndex % 2 === 1}
                        />
                    ))}
                </>
            )}
        </>
    );
}

// // // //

export function NetworkTable(props: { networks: QueryResponse<Network> }) {
    const { networks } = props;

    // Exclude child networks from the table (they're rendered in the expandable rows)
    const networkTableData = getNetworkTableData(networks.results).filter(
        (n) => n.parent_network_id === null,
    );

    const { query, setQuery, sortedItems } = useInMemoryNetworkSort({
        rows: networkTableData,
        sort: {
            field: NetworkTableDataColumns.distributedMarketCap,
            direction: SortDirections.desc,
        },
    });

    const columns = [
        RANK_COLUMN,
        NAME_INDIVIDUAL_COLUMN,

        RWA_ASSET_COUNT_COLUMN,
        DISTRIBUTED_MARKET_CAP_COLUMN,
        ISOLATED_MARKET_CAP_COLUMN,
        PCT_DISTRIBURTED_COLUMN,
        RWA_MARKET_CAP_COLUMN,
        RWA_HOLDERS_COUNT_COLUMN,

        STABLECOIN_ASSET_COUNT_COLUMN,
        STABLECOIN_MARKET_CAP_COLUMN,
        STABLECOIN_HOLDERS_COUNT_COLUMN,
    ];

    return (
        <DataGrid
            query={query}
            columns={columns}
            rows={sortedItems}
            rowKey="id"
            bordered
            size={RowSizes.small}
            loading={false}
            analyticsTableName="network-list"
            onChangeQuery={setQuery}
            enableItemsPerPageSelect
        >
            {({ row, rowIndex, scrollLeft }) => (
                <NetworkTableRow
                    network={{ ...row, rank: rowIndex + 1 }}
                    key={row.id}
                    columns={columns}
                    size={RowSizes.small}
                    bordered
                    scrollLeft={scrollLeft}
                    striped={rowIndex % 2 === 1}
                    rowIndex={rowIndex}
                    defaultExpandedState={false}
                />
            )}
        </DataGrid>
    );
}
